import type { NoteRow } from '../query/notes';
import type { CommonLoadParams } from './playback-state';

type NotePlaybackSource = Pick<
  NoteRow,
  | 'audio_conversion_id'
  | 'doc_pack_page'
  | 'doc_pack_char_start'
  | 'chapter_audio_offset_ms'
  | 'tts_id'
>;

type LegacyNoteTarget = {
  readonly ttsId: string;
  readonly loadParams: {
    readonly loadPos: {
      readonly posMs: number;
    };
  };
};

export type NotePlaybackTarget =
  | {
      readonly type: 'doc-pack';
      readonly audioConversionId: string;
      readonly anchor: {
        readonly page: number;
        readonly charStart: number;
      };
      readonly loadParams: {
        readonly loadPos: {
          readonly docPackPage: number;
          readonly docPackCharStart: number;
        };
      };
      readonly legacy: LegacyNoteTarget | undefined;
    }
  | ({
      readonly type: 'legacy';
      readonly audioConversionId: string;
    } & LegacyNoteTarget);

const legacyNoteTarget = (note: NotePlaybackSource) =>
  note.tts_id
    ? {
        ttsId: note.tts_id,
        loadParams: { loadPos: { posMs: note.chapter_audio_offset_ms ?? 0 } },
      }
    : undefined;

export function getNotePlaybackTarget(
  note: NotePlaybackSource,
): NotePlaybackTarget | undefined {
  const legacy = legacyNoteTarget(note);
  if (note.doc_pack_page !== null && note.doc_pack_char_start !== null) {
    return {
      type: 'doc-pack',
      audioConversionId: note.audio_conversion_id,
      anchor: {
        page: note.doc_pack_page,
        charStart: note.doc_pack_char_start,
      },
      loadParams: {
        loadPos: {
          docPackPage: note.doc_pack_page,
          docPackCharStart: note.doc_pack_char_start,
        },
      },
      legacy,
    };
  }

  if (legacy) {
    return {
      type: 'legacy',
      audioConversionId: note.audio_conversion_id,
      ...legacy,
    };
  }
}

/** Where a load carrying this note should start, in the same shape
 * `audioProgressLoadArgs` gives saved progress — the load path uses one in
 * place of the other. A doc-pack note keeps its legacy chunk as the fallback
 * for a player that is not using the doc-pack. */
export function noteTargetLoadArgs(target: NotePlaybackTarget): {
  chapterId: string | undefined;
  ttsId: string | undefined;
  loadParams: CommonLoadParams;
} {
  return target.type === 'doc-pack'
    ? {
        chapterId: undefined,
        ttsId: target.legacy?.ttsId,
        loadParams: {
          ...(target.legacy?.loadParams ?? target.loadParams),
          docPackFrom: target.anchor,
        },
      }
    : {
        chapterId: undefined,
        ttsId: target.ttsId,
        loadParams: { ...target.loadParams },
      };
}

type NotePlaybackAnchorFieldsInput = {
  readonly docPackAnchor?: {
    readonly page: number;
    readonly charStart: number;
    readonly charEnd: number;
  };
  readonly chapterId?: string | null | undefined;
  readonly ttsId?: string | null | undefined;
  readonly positionMs: number;
};

export function notePlaybackAnchorFields({
  chapterId,
  docPackAnchor,
  positionMs,
  ttsId,
}: NotePlaybackAnchorFieldsInput) {
  if (docPackAnchor) {
    return {
      chapter_id: null,
      chapter_audio_offset_ms: null,
      tts_id: null,
      doc_pack_page: docPackAnchor.page,
      doc_pack_char_start: docPackAnchor.charStart,
      doc_pack_char_end: docPackAnchor.charEnd,
    };
  }

  return {
    chapter_id: chapterId ?? null,
    // Round: the player reports `el.currentTime * 1000`, a float, and the
    // contract's `chapter_audio_offset_ms` is `Schema.Int` (the column is
    // `integer`). An unrounded value fails payload encoding client-side, so the
    // request is never sent — same rounding the progress writer does.
    chapter_audio_offset_ms: Math.round(positionMs),
    tts_id: ttsId ?? null,
    doc_pack_page: null,
    doc_pack_char_start: null,
    doc_pack_char_end: null,
  };
}

export const docPackAnchorId = (anchor: {
  readonly page: number;
  readonly charStart: number;
  readonly charEnd?: number | undefined;
}) =>
  `doc-pack:${anchor.page.toString()}:${anchor.charStart.toString()}-${(anchor.charEnd ?? anchor.charStart).toString()}`;
